import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * What you get — set as a table of contents.
 *
 * This was four cards in a grid, each with an icon in a tinted square and a
 * hover shadow. Cards in a row are the single most generic thing a landing page
 * can do, and with radius at 0 they read as four grey boxes.
 *
 * A contents page is the honest structure for "here are four things": a mono
 * numeral in the margin, the entry, a rule between. It also scans far better —
 * you read down one column instead of zig-zagging a grid — and it degrades to
 * one column on a phone without changing shape.
 *
 * No icons. The four features are conceptual (roles, anonymity, languages);
 * every icon for them would be arbitrary, and arbitrary icons are noise.
 */
const KEYS = ['reviewForm', 'rolesPerConference', 'lifecycle', 'trilingual'];

const FeaturesSection = () => {
  const { t } = useTranslation('conferences');

  return (
    <section className="bg-card border-y border-border">
      <div className="page-shell px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl mb-12 md:mb-16">
          <h2 className="font-heading font-bold text-foreground text-[26px] sm:text-[32px] leading-[1.15]">
            {t('landing.features.title')}{' '}
            <span className="italic font-academic font-normal">
              {t('landing.features.titleHighlight')}
            </span>
          </h2>
          <p className="mt-4 font-academic text-[17px] leading-[1.7] text-muted-foreground">
            {t('landing.features.subtitle')}
          </p>
        </div>

        <ol className="border-t border-border">
          {KEYS.map((key, i) => (
            <li key={key} className="border-b border-border">
              <div className="grid grid-cols-1 md:grid-cols-[4rem_minmax(0,22rem)_minmax(0,1fr)] gap-x-6 gap-y-2 py-7 md:py-8">
                {/* The numeral sits in the margin, mono and tabular, the way a
                    contents page numbers its entries. */}
                <span className="font-mono text-[13px] tabular-nums text-muted-foreground pt-1" dir="ltr">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-heading font-bold text-foreground text-[19px] leading-[1.3]">
                  {t(`landing.features.${key}.title`)}
                </h3>
                <p className="font-academic text-[16px] leading-[1.7] text-muted-foreground">
                  {t(`landing.features.${key}.description`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default FeaturesSection;
